import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { deleteMavenirConfigs } from '@api/dataSources';

import { IMavenirConfigFilter } from '@interfaces/cmModule/dataSources';
import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TConfigMutation } from '@interfaces/common/handlers';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import {
	MAVENIR_CONFIG_DATES,
	MAVENIR_CONFIG_GNB_IDS,
	MAVENIR_CONFIG_IDS,
	MAVENIR_CONFIG_TYPES,
	MAVENIR_CONFIG_VERSIONS,
	MAVENIR_CONFIGS,
	MAVENIR_CONFIGS_DELETE,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IMavenirConfigsDeleteReturnType {
	deleteConfigs: (dto: IGroupOperationWithActionDTO) => void;
	isLoadingDeleteConfigs: boolean;
}

export function useMutateMavenirConfigsDelete({
	filter,
	onSuccess,
}: TConfigMutation<IMavenirConfigFilter>): IMavenirConfigsDeleteReturnType {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		mutate: deleteConfigs,
		isLoading: isLoadingDeleteConfigs,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [MAVENIR_CONFIGS_DELETE],
		mutationFn: async ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			await deleteMavenirConfigs({
				filter: (actionType === EMenuActionsTypes.allFiltered) && filter
					? filter
					: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: () => {
			if (onSuccess) onSuccess();

			dispatch(setSnackbar({
				message: responseMessages.deleteDataSourceConfig,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([MAVENIR_CONFIGS]),
				queryClient.invalidateQueries([MAVENIR_CONFIG_GNB_IDS]),
				queryClient.invalidateQueries([MAVENIR_CONFIG_VERSIONS]),
				queryClient.invalidateQueries([MAVENIR_CONFIG_TYPES]),
				queryClient.invalidateQueries([MAVENIR_CONFIG_IDS]),
				queryClient.invalidateQueries([MAVENIR_CONFIG_DATES]),
			];
		},
	});

	return {
		deleteConfigs,
		isLoadingDeleteConfigs,
	};
}
